import React, {useEffect} from 'react';
import {v4 as uuid} from 'uuid';
import {Stack} from '@mui/material';
import {Modal} from '@lightricks/react-design-system';
import usePurchaseProcessEndedEvent from '@contentlab/hooks/analytics/use-purchase-process-ended-event';
import useStoreFlowStartedEvent from '@contentlab/hooks/analytics/use-store-flow-started-event';
import useStoreViewDismissedEvent from '@contentlab/hooks/analytics/use-store-view-dismissed-event';
import AnalyticsService from '@/services/analytics/AnalyticsService';
import {
  CONTENT_STUDIO_PRICING_SOURCES,
  CONTENT_STUDIO_PRICING_VIEW_TYPES,
  CONTENT_STUDIO_VIEW_NAMES,
  FLOW_NAMES,
} from '@/lib/analytics/analyticsConstants';
import ContentLabPlanBundle from '@/types/models/pricing/contentLabPlanBundle';
import SubscriptionChangeContent from '@/views/content-studio/pricing/SubscriptionChangeModalContent';
import UpgradeModalContent from '@/views/content-studio/pricing/UpgradeModalContent';
import useContentLabSubscriptionData from '@/hooks/use-content-lab-subscription-data';
import useIsMobile from '@/hooks/use-is-mobile';
import useContentStudioPricingModalStore, {
  contentStudioPricingModalActions,
  ModalState,
} from '@/stores/contentStudioPricingModalStore';
import styles from './ContentStudioPricingModal.module.scss';

function ContentStudioPricingModal() {
  const {
    state,
    subscriptionChangePlans,
    navigationContext,
    onCancelCallback,
    onSuccessCallback,
  } = useContentStudioPricingModalStore();

  const isMobile = useIsMobile();
  const {storeFlowStarted} = useStoreFlowStartedEvent(
    FLOW_NAMES.CONTENT_STUDIO.PRICING
  );
  const {storeViewDismissed} = useStoreViewDismissedEvent(
    FLOW_NAMES.CONTENT_STUDIO.PRICING
  );
  const {purchaseProcessEnded} = usePurchaseProcessEndedEvent(
    FLOW_NAMES.CONTENT_STUDIO.PRICING
  );

  const skippedFlowStartedEventSources = [
    CONTENT_STUDIO_PRICING_SOURCES.PRICING_PAGE,
    CONTENT_STUDIO_PRICING_SOURCES.PRICING_MODAL,
    CONTENT_STUDIO_PRICING_SOURCES.SUBSCRIPTION_CHANGE_CONFIRM_MODAL,
  ];

  const {
    activePlan,
    activeSubscription,
    plansCollections,
    isPlansDataFetched,
    isFetchingCurrentSubscription,
    refetchCurrentSubscription,
  } = useContentLabSubscriptionData();

  const businessCollection = plansCollections.find(
    (c) => c.bundle === ContentLabPlanBundle.BUSINESS
  );

  const purchaseProcessId = uuid();

  useEffect(() => {
    // Assuming the flow has been initialized with the source before redirecting to the pricing page.
    const flow = AnalyticsService.getOrCreateFlow(
      FLOW_NAMES.CONTENT_STUDIO.PRICING
    );
    AnalyticsService.updateFlow(FLOW_NAMES.CONTENT_STUDIO.PRICING, {
      screenPresentationId:
        AnalyticsService.getActiveScreenPresented().screen_presentation_id,
    });
    if (state === ModalState.UPGRADE) {
      if (
        !flow.source ||
        !skippedFlowStartedEventSources.includes(flow.source)
      ) {
        storeFlowStarted({
          flow_id: flow.flow_id,
          product_type: 'subscription',
          source: flow.source || '',
        });
      }
    }
  }, [state]);

  const handleClose = (shouldSendProcessCanceledEvent = true) => {
    if (state === ModalState.CHANGE_CONFIRM && shouldSendProcessCanceledEvent) {
      purchaseProcessEnded({
        presentation_id: AnalyticsService.getFlow(
          FLOW_NAMES.CONTENT_STUDIO.PRICING
        ).screen_presentation_id,
        process_id: purchaseProcessId,
        product_id: subscriptionChangePlans?.selectedPlan.id || '',
        product_name: subscriptionChangePlans?.selectedPlan.id || '',
        result: 'cancel',
      });
    }
    storeViewDismissed({
      presentation_id: AnalyticsService.getFlow(
        FLOW_NAMES.CONTENT_STUDIO.PRICING
      ).screen_presentation_id,
      view_name: CONTENT_STUDIO_VIEW_NAMES.PRICING,
      view_type:
        state === ModalState.UPGRADE
          ? CONTENT_STUDIO_PRICING_VIEW_TYPES.PRICING_MODAL
          : CONTENT_STUDIO_PRICING_VIEW_TYPES.CHANGE_CONFIRM_MODAL,
    });
    contentStudioPricingModalActions.closeModal();
    onCancelCallback?.();
  };

  const handleBackButtonClick = (navigation: {
    goBack: () => void;
    navigate: (to: string, params?: any) => void;
  }) => {
    if (navigationContext === 'direct_to_confirm') {
      handleClose();
    } else if (navigationContext === 'via_upgrade') {
      storeViewDismissed({
        presentation_id: AnalyticsService.getFlow(
          FLOW_NAMES.CONTENT_STUDIO.PRICING
        ).screen_presentation_id,
        view_name: CONTENT_STUDIO_VIEW_NAMES.PRICING,
        view_type: CONTENT_STUDIO_PRICING_VIEW_TYPES.CHANGE_CONFIRM_MODAL,
      });
      AnalyticsService.updateFlow(FLOW_NAMES.CONTENT_STUDIO.PRICING, {
        source:
          CONTENT_STUDIO_PRICING_SOURCES.SUBSCRIPTION_CHANGE_CONFIRM_MODAL,
      });
      contentStudioPricingModalActions.openUpgradeModal({
        source:
          CONTENT_STUDIO_PRICING_SOURCES.SUBSCRIPTION_CHANGE_CONFIRM_MODAL,
      });
    } else {
      handleClose();
    }
  };

  return (
    <Modal
      showCloseButton
      open={state !== ModalState.CLOSED}
      testID="content-studio-pricing-modal"
      width={800}
      handleClose={() => handleClose()}
      fullScreen={isMobile}
      contentClassName={styles.modalContent}
    >
      <Stack className={styles.container}>
        <Stack className={styles.contentArea}>
          {state === ModalState.UPGRADE ? (
            <UpgradeModalContent
              isLoading={!isPlansDataFetched || isFetchingCurrentSubscription}
              plans={businessCollection?.plans || []}
              activePlan={activePlan}
              activeSubscriptionId={activeSubscription?.id}
              handleClose={handleClose}
              presentationId={
                AnalyticsService.getFlow(FLOW_NAMES.CONTENT_STUDIO.PRICING)
                  .screen_presentation_id
              }
            />
          ) : (
            subscriptionChangePlans && (
              <SubscriptionChangeContent
                currentSubscriptionId={
                  subscriptionChangePlans.currentSubscriptionId
                }
                currentPlan={subscriptionChangePlans.currentPlan}
                selectedPlan={subscriptionChangePlans.selectedPlan}
                onBackButtonClick={handleBackButtonClick}
                handleClose={handleClose}
                presentationId={
                  AnalyticsService.getFlow(FLOW_NAMES.CONTENT_STUDIO.PRICING)
                    .screen_presentation_id
                }
                purchaseProcessId={purchaseProcessId}
                onSuccessCallback={onSuccessCallback}
                refetchCurrentSubscription={refetchCurrentSubscription}
              />
            )
          )}
        </Stack>
        <img
          src="https://storage.googleapis.com/brands-public-assets/content-lab/PricingModal.png"
          alt="Pricing Modal"
          className={styles.image}
        />
      </Stack>
    </Modal>
  );
}

export default ContentStudioPricingModal;
